import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  auth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  setPersistence,
  browserLocalPersistence,
  getAuth,
  signInWithCustomToken
} from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp, query, collection, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useLanguage } from './LanguageContext';
import translations from '../translations';
import { logError } from '../services/errorService';
import { logAuthEvent, LOG_LEVELS } from '../services/loggingService';
import { warmAuthToken } from '../services/vectorSearchService';
import LogRocket from 'logrocket';

// Create the authentication context
const AuthContext = createContext();

// Session duration constant
const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Custom hook to use the auth context
export function useAuth() {
  return useContext(AuthContext);
}

// Provider component that wraps the app and makes auth object available to any child component that calls useAuth()
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [userRole, setUserRole] = useState('member'); // admin or member
  const [trialInfo, setTrialInfo] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState('');

  const actionCodeSettings = {
    url: window.location.origin,
    handleCodeInApp: true,
  };

  // Function to refresh the Firebase ID token
  const refreshToken = async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await currentUser.getIdToken(true);
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
    }
  };

  // Function to refresh organization data
  const refreshOrganizationData = async () => {
    try {
      if (!currentUser) return;
      
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        if (userData.orgId) {
          const orgDocRef = doc(db, 'organizations', userData.orgId);
          const orgDoc = await getDoc(orgDocRef);
          
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            
            // Update organization state
            setCurrentOrg({ id: orgDoc.id, ...orgData });
            
            // Update trial info
            if (orgData.trialInfo) {
              const remainingDays = calculateRemainingTrialDays(orgData.trialInfo.trialEndDate);
              setTrialInfo({
                ...orgData.trialInfo,
                trialDaysRemaining: remainingDays
              });
            }
            
            // Update subscription info
            if (orgData.subscription) {
              setSubscription(orgData.subscription);
            }
          }
        }
      }
    } catch (error) {
      console.error("Organization data refresh failed:", error);
    }
  };

  // Function to sign in with Google
  async function googleSignIn() {
    try {
      setError('');
      const provider = new GoogleAuthProvider();
      
      // Use redirect method instead of popup to avoid COOP policy issues
      await signInWithRedirect(auth, provider);
      // Note: The result will be handled in the onAuthStateChanged listener
      // No return value needed here as the page will redirect
      
      return null;
    } catch (err) {
      // Use error service and log the error
      const errorMessage = logError(err, { action: 'googleSignIn' });
      
      // Log failed login attempt
      logAuthEvent('google_login_failed', 
        { errorCode: err.code }, 
        LOG_LEVELS.WARNING
      );
      
      setError(errorMessage);
      throw err;
    }
  }

  // Function to sign in with Google using popup
  async function googleSignInWithPopup() {
    try {
      setError('');
      console.log('Starting Google sign-in with popup');
      const provider = new GoogleAuthProvider();
      
      // Use popup method instead of redirect
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      console.log('Google sign-in with popup successful:', user.email);
      
      // Check if the user document exists in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      // If user doesn't exist, create a new document
      if (!userDoc.exists()) {
        console.log('Creating new user document for popup sign-in:', user.email);
        
        // Check if there's an existing invited user document with this email
        const existingInviteQuery = query(
          collection(db, 'users'),
          where('email', '==', user.email.toLowerCase()),
          where('status', '==', 'invited')
        );
        
        const inviteSnapshot = await getDocs(existingInviteQuery);
        let userDocData;
        
        if (!inviteSnapshot.empty) {
          // Found an existing invite - preserve the organization data
          const inviteDoc = inviteSnapshot.docs[0];
          const inviteData = inviteDoc.data();
          
          console.log(`Found existing invite for ${user.email}, preserving organization data`);
          
          userDocData = {
            ...inviteData, // Preserve all invite data (orgId, role, inviteToken, etc.)
            displayName: user.displayName || inviteData.displayName || '',
            emailVerified: user.emailVerified, // Use verification status from Google
            status: 'invited', // Keep as invited so they can use the org setup flow
            updatedAt: serverTimestamp()
          };
          
          // Delete the old invite document since we're updating it with the new UID
          await deleteDoc(inviteDoc.ref);
        } else {
          // No existing invite - create a standard new user document
          userDocData = {
            email: user.email,
            displayName: user.displayName || '',
            emailVerified: user.emailVerified, // Use verification status from Google
            status: 'pending_org_setup', // Will be updated when org is set up
            isGlobalAdmin: false,
            createdAt: serverTimestamp()
          };
        }
        
        await setDoc(userDocRef, userDocData);
      }
      
      // Log successful login
      logAuthEvent('google_popup_login_success', 
        { email: user.email, name: user.displayName }, 
        LOG_LEVELS.INFO, 
        user.uid
      );
      
      // Warm up authentication token for vector search
      warmAuthToken().catch(error => {
        console.warn('Failed to warm authentication token on Google login:', error);
      });
      
      // We don't need to setCurrentUser here as the onAuthStateChanged listener will handle it
      return user;
    } catch (err) {
      // Handle errors
      console.error('Google popup sign-in error:', err);
      const errorMessage = logError(err, { action: 'googleSignInWithPopup' });
      
      if (err.code !== 'auth/popup-closed-by-user' && 
          err.code !== 'auth/cancelled-popup-request') {
        logAuthEvent('google_popup_login_failed', 
          { errorCode: err.code }, 
          LOG_LEVELS.WARNING
        );
        setError(errorMessage);
      }
      
      throw err;
    }
  }

  // Function to sign in with Microsoft
  async function microsoftSignIn() {
    try {
      setError('');
      const provider = new OAuthProvider('microsoft.com');
      
      // Use redirect method for Microsoft auth
      await signInWithRedirect(auth, provider);
      // The result will be handled in the onAuthStateChanged listener
      
      return null;
    } catch (err) {
      // Use error service and log the error
      const errorMessage = logError(err, { action: 'microsoftSignIn' });
      
      // Log failed login attempt
      logAuthEvent('microsoft_login_failed', 
        { errorCode: err.code }, 
        LOG_LEVELS.WARNING
      );
      
      setError(errorMessage);
      throw err;
    }
  }

  // Function to sign in with Microsoft using popup
  async function microsoftSignInWithPopup() {
    try {
      setError('');
      console.log('Starting Microsoft sign-in with popup');
      const provider = new OAuthProvider('microsoft.com');
      
      // Use popup method instead of redirect
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      console.log('Microsoft sign-in with popup successful:', user.email);
      
      // Check if the user document exists in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      // If user doesn't exist, create a new document
      if (!userDoc.exists()) {
        console.log('Creating new user document for Microsoft popup sign-in:', user.email);
        
        // Check if there's an existing invited user document with this email
        const existingInviteQuery = query(
          collection(db, 'users'),
          where('email', '==', user.email.toLowerCase()),
          where('status', '==', 'invited')
        );
        
        const inviteSnapshot = await getDocs(existingInviteQuery);
        let userDocData;
        
        if (!inviteSnapshot.empty) {
          // Found an existing invite - preserve the organization data
          const inviteDoc = inviteSnapshot.docs[0];
          const inviteData = inviteDoc.data();
          
          console.log(`Found existing invite for ${user.email}, preserving organization data`);
          
          userDocData = {
            ...inviteData, // Preserve all invite data (orgId, role, inviteToken, etc.)
            displayName: user.displayName || inviteData.displayName || '',
            emailVerified: user.emailVerified, // Use verification status from Microsoft
            status: 'invited', // Keep as invited so they can use the org setup flow
            updatedAt: serverTimestamp()
          };
          
          // Delete the old invite document since we're updating it with the new UID
          await deleteDoc(inviteDoc.ref);
        } else {
          // No existing invite - create a standard new user document
          userDocData = {
            email: user.email,
            displayName: user.displayName || '',
            emailVerified: user.emailVerified, // Use verification status from Microsoft
            status: 'pending_org_setup', // Will be updated when org is set up
            isGlobalAdmin: false,
            createdAt: serverTimestamp()
          };
        }
        
        await setDoc(userDocRef, userDocData);
      }
      
      // Log successful login
      logAuthEvent('microsoft_popup_login_success', 
        { email: user.email, name: user.displayName }, 
        LOG_LEVELS.INFO, 
        user.uid
      );
      
      // Warm up authentication token for vector search
      warmAuthToken().catch(error => {
        console.warn('Failed to warm authentication token on Microsoft login:', error);
      });
      
      // We don't need to setCurrentUser here as the onAuthStateChanged listener will handle it
      return user;
    } catch (err) {
      // Handle errors
      console.error('Microsoft popup sign-in error:', err);
      const errorMessage = logError(err, { action: 'microsoftSignInWithPopup' });
      
      if (err.code !== 'auth/popup-closed-by-user' && 
          err.code !== 'auth/cancelled-popup-request') {
        logAuthEvent('microsoft_popup_login_failed', 
          { errorCode: err.code }, 
          LOG_LEVELS.WARNING
        );
        setError(errorMessage);
      }
      
      throw err;
    }
  }

  // Function to sign up a new user
  async function signup(email, password, displayName) {
    try {
      setError('');
      
      const passwordErrors = validatePassword(password);
      if (passwordErrors.length > 0) {
        setError(getTranslation('errors.passwordRequirements'));
        throw new Error('Password validation failed');
      }
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName });
      
      // Send email verification WITH redirect settings
      await sendEmailVerification(userCredential.user, actionCodeSettings);
      console.log(`Verification email sent to ${email} with redirect URL: ${actionCodeSettings.url}`);
      
      // Check if there's an existing invited user document with this email
      const existingInviteQuery = query(
        collection(db, 'users'),
        where('email', '==', email.toLowerCase()),
        where('status', '==', 'invited')
      );
      
      const inviteSnapshot = await getDocs(existingInviteQuery);
      let userDocData;
      
      if (!inviteSnapshot.empty) {
        // Found an existing invite - preserve the organization data
        const inviteDoc = inviteSnapshot.docs[0];
        const inviteData = inviteDoc.data();
        
        console.log(`Found existing invite for ${email}, preserving organization data`);
        
        userDocData = {
          ...inviteData, // Preserve all invite data (orgId, role, inviteToken, etc.)
          displayName: displayName || inviteData.displayName || '',
          emailVerified: false, // Starts as false for new signup
          status: 'invited', // Keep as invited so they can use the org setup flow
          updatedAt: serverTimestamp()
        };
        
        // Delete the old invite document since we're updating it with the new UID
        await deleteDoc(inviteDoc.ref);
      } else {
        // No existing invite - create a standard new user document
        console.log(`No existing invite found for ${email}, creating new user document`);
        
        userDocData = {
          email: userCredential.user.email,
          displayName: displayName || '',
          emailVerified: false, // Starts as false
          status: 'pending_org_setup', // Will be updated when org is set up
          isGlobalAdmin: false,
          createdAt: serverTimestamp()
        };
      }
      
      // Create the user document with the new Firebase UID
      await setDoc(doc(db, 'users', userCredential.user.uid), userDocData);
      
      logAuthEvent('signup_success', { email, displayName, hadExistingInvite: !inviteSnapshot.empty }, LOG_LEVELS.INFO, userCredential.user.uid);
      
      return userCredential.user;
    } catch (err) {
      const errorMessage = logError(err, { action: 'signup', email, displayName });
      logAuthEvent('signup_failed', { email, errorCode: err.code, errorMessage: err.message }, LOG_LEVELS.WARNING);
      if (err.message !== 'Password validation failed') {
        setError(errorMessage);
      }
      throw err;
    }
  }

  // Function to resend verification email
  async function resendVerificationEmail() {
    try {
      setError('');
      
      if (!currentUser) {
        setError('You must be logged in to resend verification email');
        throw new Error('No user is currently logged in');
      }
      
      if (currentUser.emailVerified) {
        console.log('Email already verified, no need to resend.');
        return true; // Or throw an error/set message
      }
      
      // Send email verification WITH redirect settings
      await sendEmailVerification(currentUser, actionCodeSettings);
      console.log(`Verification email resent to ${currentUser.email} with redirect URL: ${actionCodeSettings.url}`);
      
      logAuthEvent('verification_email_resent', { email: currentUser.email }, LOG_LEVELS.INFO, currentUser.uid);
      
      return true;
    } catch (err) {
      const errorMessage = logError(err, { action: 'resendVerificationEmail' });
      logAuthEvent('verification_email_resend_failed', { errorCode: err.code }, LOG_LEVELS.WARNING, currentUser?.uid);
      setError(errorMessage);
      throw err;
    }
  }

  // Function to log in a user
  async function login(email, password) {
    try {
      setError('');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Update Firestore with the latest verification status (optional but good practice)
      try {
          const userDocRef = doc(db, 'users', userCredential.user.uid);
          await setDoc(userDocRef, { emailVerified: userCredential.user.emailVerified }, { merge: true });
      } catch (dbError) {
          console.error("Error updating emailVerified status in Firestore:", dbError);
          // Don't fail the login if Firestore update fails
      }
      
      // Identify user in LogRocket
      LogRocket.identify(userCredential.user.uid, {
        name: userCredential.user.displayName || 'Unknown User',
        email: userCredential.user.email || '',
        emailVerified: userCredential.user.emailVerified
      });
      
      logAuthEvent('login_success', { email }, LOG_LEVELS.INFO, userCredential.user.uid);
      
      // Warm up authentication token for vector search
      warmAuthToken().catch(error => {
        console.warn('Failed to warm authentication token on login:', error);
      });
      
      return userCredential.user; // AppContent will handle routing based on verification
    } catch (err) {
      const errorMessage = logError(err, { action: 'login', email });
      logAuthEvent('login_failed', { email, errorCode: err.code }, LOG_LEVELS.WARNING);
      setError(errorMessage);
      throw err;
    }
  }

  // Function to log out the current user
  async function logout() {
    try {
      setError('');
      
      // Log logout attempt before actually logging out
      if (currentUser) {
        logAuthEvent('logout', 
          { uid: currentUser.uid, email: currentUser.email }, 
          LOG_LEVELS.INFO, 
          currentUser.uid
        );
      }
      
      await signOut(auth);
    } catch (err) {
      // Use error service and log the error
      const errorMessage = logError(err, { action: 'logout', userId: currentUser?.uid });
      
      // Log failed logout
      logAuthEvent('logout_failed', 
        { errorCode: err.code }, 
        LOG_LEVELS.WARNING,
        currentUser?.uid
      );
      
      setError(errorMessage);
      throw err;
    }
  }

  // Function to send a password reset email
  async function resetPassword(email) {
    try {
      setError('');
      await sendPasswordResetEmail(auth, email);
      
      // Log password reset request
      logAuthEvent('password_reset_requested', 
        { email }, 
        LOG_LEVELS.INFO
      );
    } catch (err) {
      // Use error service and log the error
      const errorMessage = logError(err, { action: 'resetPassword', email });
      
      // Log failed password reset
      logAuthEvent('password_reset_failed', 
        { email, errorCode: err.code }, 
        LOG_LEVELS.WARNING
      );
      
      setError(errorMessage);
      throw err;
    }
  }

  // Function to convert Firebase error codes to readable messages - Not needed anymore, using logError
  function getReadableErrorMessage(errorCode) {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'This email is already registered.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/too-many-requests':
        return 'Too many unsuccessful login attempts. Please try again later.';
      default:
        return 'An error occurred. Please try again.';
    }
  }

  // Function to validate password
  function validatePassword(password) {
    const errors = [];
    
    if (password.length < 10) {
      errors.push(getTranslation('errors.passwordMinLength'));
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push(getTranslation('errors.passwordUppercase'));
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push(getTranslation('errors.passwordLowercase'));
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push(getTranslation('errors.passwordNumber'));
    }
    
    if (!/[^A-Za-z0-9]/.test(password)) {
      errors.push(getTranslation('errors.passwordSpecial'));
    }
    
    return errors;
  }

  // Function to get translation based on current language
  const getTranslation = (key) => {
    const lang = localStorage.getItem('language') || 'en';
    const translationObj = translations[lang] || translations.en;
    
    // Parse the key path
    const keyParts = key.split('.');
    
    // Navigate to the correct location in the object
    let result = translationObj;
    for (const part of keyParts) {
      if (!result[part]) {
        // Fallback to English if translation not found
        if (lang !== 'en') {
          // Avoid infinite loop by directly returning key if English fallback also fails
          const enResult = (translations.en || {})[part];
          if (!enResult) return key; 
          result = enResult;
        } else {
          return key;
        }
      } else {
        result = result[part];
      }
    }
    
    // Ensure the final result is a string
    if (typeof result !== 'string') {
        console.warn(`Translation key '${key}' did not resolve to a string in language '${lang}'.`);
        return key; // Return key if not a string
    }
    
    return result;
  };

  // Function to sign in with a custom token
  async function signInWithProvidedCustomToken(token) {
    try {
      setLoading(true); // Indicate loading during custom token sign-in
      setError('');
      console.log('Attempting sign-in with custom token...');
      const userCredential = await signInWithCustomToken(auth, token);
      console.log('Sign-in with custom token successful:', userCredential.user.uid);
      // The onAuthStateChanged listener will handle setting the currentUser and userRole
      // Log successful login
      logAuthEvent('custom_token_login_success', 
        { uid: userCredential.user.uid }, 
        LOG_LEVELS.INFO, 
        userCredential.user.uid
      );
      
      // Warm up authentication token for vector search
      warmAuthToken().catch(error => {
        console.warn('Failed to warm authentication token on custom token login:', error);
      });
      
      return userCredential.user;
    } catch (err) {
      console.error('Error signing in with custom token:', err);
      const errorMessage = logError(err, { action: 'signInWithCustomToken' });
      logAuthEvent('custom_token_login_failed', 
        { errorCode: err.code }, 
        LOG_LEVELS.ERROR
      );
      setError('Failed to complete sign-in session. Please try logging in manually.');
      setLoading(false); // Ensure loading is false on error
      throw err;
    }
    // No finally setLoading(false) here, as onAuthStateChanged handles the final loading state
  }

  // Set persistence BEFORE other auth checks
  useEffect(() => {
    let isMounted = true;
    console.log('Setting auth persistence to local');
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log('Auth persistence set to local successfully.');
        // Persistence set, now Firebase can initialize and onAuthStateChanged will fire
      })
      .catch((error) => {
        console.error("Error setting auth persistence: ", error);
        if (isMounted) {
          setError("Could not initialize session. Please ensure cookies/local storage are enabled.");
          setLoading(false); 
        }
      });
    
    return () => { isMounted = false; }
  }, []); 

  // useEffect hook to check for custom token in URL on mount
  useEffect(() => {
    let isMounted = true;
    const urlParams = new URLSearchParams(window.location.search);
    const customToken = urlParams.get('customToken');

    if (customToken) {
      console.log('Custom token found in URL.');
      // Remove the token from the URL immediately
      const newUrl = window.location.pathname + window.location.hash; // Keep path and hash, remove query
      window.history.replaceState({}, document.title, newUrl);
      
      // Attempt sign-in with the custom token
      signInWithProvidedCustomToken(customToken).catch(err => {
        // Error is already logged in the function, maybe set an error message
        console.error("Caught error from signInWithProvidedCustomToken call");
        if (isMounted) {
           // setError is already set within the function, maybe append info
           // setError(prev => prev ? prev + " (Custom Token)" : "Sign-in failed (Custom Token)");
        }
      });
    }
    
    return () => { isMounted = false; }
  }, []); // Run only once on mount

  // Calculate remaining trial days
  const calculateRemainingTrialDays = (trialEndDate) => {
    if (!trialEndDate) return 0;
    
    const endDate = trialEndDate instanceof Date ? trialEndDate : trialEndDate.toDate();
    const now = new Date();
    
    // Calculate the difference in days
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Return 0 if trial has ended
    return Math.max(0, diffDays);
  };

  // Main Auth Listener
  useEffect(() => {
    let isMounted = true;
    console.log('Auth context mounting: Setting up listener.');
    // Do not set loading(true) here, persistence useEffect handles initial loading state logic indirectly

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return;
      console.log('onAuthStateChanged triggered');
      console.log('onAuthStateChanged user:', user ? { uid: user.uid, email: user.email, verified: user.emailVerified } : 'null');
      
      if (user) {
        console.log('User found in onAuthStateChanged, processing user data...');
      } else {
        console.log('No user in onAuthStateChanged, setting unauthenticated state');
      }

      if (user) {
        // Identify user in LogRocket
        LogRocket.identify(user.uid, {
          name: user.displayName || 'Unknown User',
          email: user.email || '',
          emailVerified: user.emailVerified,
          userRole: userRole,
          hasOrganization: !!currentOrg
        });
        
        const lastSignInTime = new Date(user.metadata.lastSignInTime).getTime();
        const currentTime = Date.now();
        if (currentTime - lastSignInTime > SESSION_DURATION) {
          console.log('User session expired in listener');
          await signOut(auth);
          setError(getTranslation('auth.sessionExpired'));
        } else {
          try {
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
            let isAdmin = false;
            let firestoreVerified = false;
            let userTrialInfo = null;
            let userSubscription = null;

            if (!userDoc.exists()) {
              console.log('Creating new user document in listener:', user.email);
              
              // Check if there's an existing invited user document with this email
              const existingInviteQuery = query(
                collection(db, 'users'),
                where('email', '==', user.email.toLowerCase()),
                where('status', '==', 'invited')
              );
              
              const inviteSnapshot = await getDocs(existingInviteQuery);
              let userDocData;
              
              if (!inviteSnapshot.empty) {
                // Found an existing invite - preserve the organization data
                const inviteDoc = inviteSnapshot.docs[0];
                const inviteData = inviteDoc.data();
                
                console.log(`Found existing invite for ${user.email} in auth listener, preserving organization data`);
                
                userDocData = {
                  ...inviteData, // Preserve all invite data (orgId, role, inviteToken, etc.)
                  displayName: user.displayName || inviteData.displayName || '',
                  emailVerified: user.emailVerified,
                  status: 'invited', // Keep as invited so they can use the org setup flow
                  updatedAt: serverTimestamp()
                };
                
                // Delete the old invite document since we're updating it with the new UID
                await deleteDoc(inviteDoc.ref);
                firestoreVerified = user.emailVerified;
                
                // Set organization data immediately if they have an orgId
                if (inviteData.orgId) {
                  try {
                    const orgDocRef = doc(db, 'organizations', inviteData.orgId);
                    const orgDoc = await getDoc(orgDocRef);
                    
                    if (orgDoc.exists()) {
                      const orgData = orgDoc.data();
                      
                      // Set organization state
                      setCurrentOrg({ id: orgDoc.id, ...orgData });
                      
                      // Get trial info from organization
                      if (orgData.trialInfo) {
                        const remainingDays = calculateRemainingTrialDays(orgData.trialInfo.trialEndDate);
                        userTrialInfo = {
                          ...orgData.trialInfo,
                          trialDaysRemaining: remainingDays
                        };
                      }
                      
                      // Get subscription info from organization
                      if (orgData.subscription) {
                        userSubscription = orgData.subscription;
                      }
                    }
                  } catch (orgError) {
                    console.error('Error loading organization for invited user:', orgError);
                  }
                }
                
                // Set user role from invite data
                setUserRole(inviteData.role || 'member');
              } else {
                // No existing invite - create a standard new user document
                userDocData = {
                  email: user.email,
                  displayName: user.displayName || '',
                  emailVerified: user.emailVerified,
                  status: 'pending_org_setup',
                  isGlobalAdmin: false,
                  createdAt: serverTimestamp()
                };
                firestoreVerified = user.emailVerified;
              }
              
              await setDoc(userDocRef, userDocData);
              logAuthEvent('user_created', { email: user.email, name: user.displayName, method: user.providerData[0]?.providerId || 'email', hadExistingInvite: !inviteSnapshot.empty }, LOG_LEVELS.INFO, user.uid);
            } else {
              const userData = userDoc.data();
              
              // Check if user has organization
              if (userData.orgId) {
                // Load organization data
                const orgDocRef = doc(db, 'organizations', userData.orgId);
                const orgDoc = await getDoc(orgDocRef);
                
                if (orgDoc.exists()) {
                  const orgData = orgDoc.data();
                  
                  // Set organization state
                  setCurrentOrg({ id: orgDoc.id, ...orgData });
                  
                  // Get trial info from organization
                  if (orgData.trialInfo) {
                    const remainingDays = calculateRemainingTrialDays(orgData.trialInfo.trialEndDate);
                    userTrialInfo = {
                      ...orgData.trialInfo,
                      trialDaysRemaining: remainingDays
                    };
                  }
                  
                  // Get subscription info from organization
                  if (orgData.subscription) {
                    userSubscription = orgData.subscription;
                  }
                }
              }
              
              // Set user role and admin status
              setUserRole(userData.role || 'member');
              isAdmin = userData.isGlobalAdmin === true; // Global admin for super admin access
              firestoreVerified = userData.emailVerified === true;
              
              if (user.emailVerified && !firestoreVerified) {
                console.log(`Updating Firestore: emailVerified status for ${user.email}`);
                await setDoc(userDocRef, { emailVerified: true }, { merge: true });
                firestoreVerified = true;
              }
            }
            
            setCurrentUser(user); 
            // setUserRole and setCurrentOrg are already set above
            setTrialInfo(userTrialInfo);
            setSubscription(userSubscription);
            console.log('Setting final user state (authenticated):', user.uid, 'userRole:', userRole, 'hasOrg:', !!currentOrg, 'Verified:', user.emailVerified);
            
            // Warm up authentication token for vector search when user is authenticated
            warmAuthToken().catch(error => {
              console.warn('Failed to warm authentication token for returning user:', error);
            });

                      } catch (err) {
              console.error("Error handling user document/role in listener:", err);
              setError("Failed to load user data. Please try refreshing.");
              setCurrentUser(user);
              setCurrentOrg(null);
              setUserRole('member');
            }
        }
      } else {
        // No user is authenticated
        console.log('Setting final user state (unauthenticated)');
        setCurrentUser(null);
        setCurrentOrg(null);
        setUserRole('member');
        setTrialInfo(null);
        setSubscription(null);
        setError(''); // Clear errors on logout
        
        // REMOVED call to checkStoredToken()
      }
      
      // Ensure loading is set to false only after the first auth state is processed
      if (initialLoad) {
          console.log('Initial auth state processed, setting loading to false.');
          setLoading(false);
          setInitialLoad(false);
      } else if (!loading) {
          // If subsequent changes happen and we are not loading, ensure loading stays false
          // unless a specific operation (like manual sign-in) sets it true.
      }
    });

    // Handle potential redirect results from Google/Microsoft Sign-in
    // This runs once on mount after persistence is set
    console.log('Checking for redirect result...');
    getRedirectResult(auth)
      .then(async result => {
        console.log('getRedirectResult returned:', result ? 'User found' : 'No result', result?.user?.email);
        if (result && isMounted) {
          console.log('Redirect result processed successfully:', result.user.email);
          console.log('User details:', {
            uid: result.user.uid,
            email: result.user.email,
            emailVerified: result.user.emailVerified,
            displayName: result.user.displayName,
            providerId: result.providerId
          });
          // Firestore document creation logic is already inside the main onAuthStateChanged handler
          // Log successful login
          logAuthEvent('redirect_login_success', 
            { email: result.user.email, name: result.user.displayName, provider: result.providerId || 'unknown' }, 
            LOG_LEVELS.INFO, 
            result.user.uid
          );
          // The onAuthStateChanged listener will handle the state update
        } else if (isMounted && !auth.currentUser) {
           // Only log 'No pending result' if we are not already logged in
           console.log('No pending redirect result found on mount.');
           // Ensure loading is false if no redirect and no user yet from onAuthStateChanged
           if (initialLoad) {
              console.log('No redirect result, setting loading to false.');
              setLoading(false);
              setInitialLoad(false);
           }
        }
      })
      .catch(err => {
        if (isMounted) {
          console.error('Error processing redirect result on mount:', err);
          console.error('Full error details:', {
            code: err.code,
            message: err.message,
            stack: err.stack
          });
          const errorMessage = logError(err, { action: 'handleRedirectResultMount' });
          setError(errorMessage); 
          // Ensure loading is false even if redirect processing fails
           if (initialLoad) {
              console.log('Error in redirect result, setting loading to false.');
              setLoading(false);
              setInitialLoad(false);
           }
        }
      });

    // Cleanup function
    return () => {
      isMounted = false;
      console.log('Cleaning up auth listener.');
      unsubscribe();
    };
  // Dependency array includes initialLoad to re-run if it changes, ensuring loading logic is robust.
  // Also include getTranslation if it's potentially unstable, though ideally it shouldn't be.
  }, [initialLoad]); 

  // Set up interval to refresh token
  useEffect(() => {
    const interval = setInterval(refreshToken, 55 * 60 * 1000); // 55 minutes
    return () => clearInterval(interval);
  }, []);

  // Value object provided to consumers
  // --- Past Due Logic ---
  let isPastDue = false;
  let daysPastDue = 0;
  let currentPeriodEndDate = null;
  if (subscription && subscription.status === 'past_due' && subscription.currentPeriodEnd) {
    // Firestore Timestamp: has toDate(), Date: use as is
    if (typeof subscription.currentPeriodEnd.toDate === 'function') {
      currentPeriodEndDate = subscription.currentPeriodEnd.toDate();
    } else if (subscription.currentPeriodEnd instanceof Date) {
      currentPeriodEndDate = subscription.currentPeriodEnd;
    } else if (typeof subscription.currentPeriodEnd === 'string' || typeof subscription.currentPeriodEnd === 'number') {
      // fallback: try to parse
      currentPeriodEndDate = new Date(subscription.currentPeriodEnd);
    }
    if (currentPeriodEndDate) {
      const now = new Date();
      daysPastDue = Math.floor((now - currentPeriodEndDate) / (1000 * 60 * 60 * 24));
      isPastDue = daysPastDue >= 0; // Only true if period end is in the past
    }
  }

  const value = {
    currentUser,
    currentOrg,
    userRole,
    trialInfo,
    subscription,
    loading,
    initialLoad,
    error,
    setError,
    login,
    signup,
    logout,
    resetPassword,
    resendVerificationEmail,
    refreshToken,
    refreshOrganizationData,
    googleSignIn,
    googleSignInWithPopup,
    microsoftSignIn,
    microsoftSignInWithPopup,
    isOrgAdmin: userRole === 'admin',
    isGlobalAdmin: currentUser?.isGlobalAdmin || false,
    signInWithProvidedCustomToken,
    // --- Past Due Exposed ---
    isPastDue,
    daysPastDue,
    currentPeriodEndDate
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children} 
    </AuthContext.Provider>
  );
}